import { DesignChannel } from 'components/NewFileModal/Types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useModals } from 'almanac-ui/Modal'
import { generatePath, useHistory } from 'react-router-dom'
import { DESIGN_PATH, DOC_PATH } from 'paths'
import { Error } from 'components/ArticlePostPreview/PostPreviewBody/Error'
import { useErrorAction } from 'components/ArticlePostPreview/PostPreviewBody/hooks'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { usePostPreviewContext } from '../PostPreviewContext'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { CaptionDrawer } from 'components/Design/CaptionTab/CaptionDrawer'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'

interface Props {
  channel?: DesignChannel
  fileId: string
}

export const CHANNELS_WITHOUT_CAPTION = [
  DesignChannel.InstagramStory,
  DesignChannel.InstagramPost,
  DesignChannel.InstagramReel,
  DesignChannel.TikTokPost,
  DesignChannel.YoutubeShort,
]

export const PostValidations = observer(({ channel, fileId }: Props) => {
  const { designStore, drawersStore } = useRootStore()
  const { isMobile } = useResponsiveDesign()
  const { closeAllModals } = useModals()
  const history = useHistory()
  const openWindowSafely = useOpenWindowSafely()
  const { isChannelPostable } = useIsChannelPostable()
  const { scheduledPost, validationErrors } = usePostPreviewContext()
  const isDesign = scheduledPost?.source_type === 'Design'
  const integration = scheduledPost?.external_integration
  const errorType = scheduledPost?.error_type
  const { errorAction } = useErrorAction({ integration, errorType })
  const mediaErrorAction = {
    label: 'See Requirements',
    onPress: () =>
      openWindowSafely(
        'https://help.blaze.ai/en/articles/9911718-image-and-video-file-requirements-by-platform-connection',
      ),
  }

  const redirectToPost = () => {
    closeAllModals()
    history.push(generatePath(DOC_PATH, { slug: fileId }))
  }

  const handleGenerateCopy = () => {
    if (isMobile) {
      drawersStore.closeAllDrawers()
      drawersStore.openDrawer(CaptionDrawer)
    } else {
      closeAllModals()
      designStore?.openTab('caption')
      history.push(generatePath(DESIGN_PATH, { key: fileId }))
    }
  }

  const errorMessage = scheduledPost?.error_message
  let errors = errorMessage ? [errorMessage, ...validationErrors] : validationErrors

  let action = errorAction()
  if (!isDesign && !errorMessage && validationErrors.length > 0) action = mediaErrorAction

  if (isDesign && !scheduledPost.content && channel && !CHANNELS_WITHOUT_CAPTION.includes(channel)) {
    return (
      <Error
        errorMessages={["This post doesn't have any content. Please write or generate copy."]}
        smallLayout
        action={{
          label: 'Generate Caption',
          onPress: handleGenerateCopy,
        }}
      />
    )
  }

  if (!isDesign && !scheduledPost?.content) {
    return (
      <Error
        errorMessages={["This post doesn't have any content"]}
        smallLayout
        action={{
          label: 'Add Content',
          onPress: redirectToPost,
        }}
      />
    )
  }

  if (isDesign && isChannelPostable(channel) && !scheduledPost.media_objects.length && !errors.length) {
    return (
      <Error errorMessages={['This post is still being processed. Please try again in a few minutes.']} smallLayout />
    )
  }

  if (errors.length > 0) return <Error errorMessages={errors} smallLayout action={action} />

  return null
})
